import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock3, Code2 } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { PeriodSelector } from '@/components/analytics/PeriodSelector'
import { Badge, Card, ToneBadge } from '@/components/ui'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import { PEDAGOGY_SKIP_REASON_TONE as TONE } from '@/lib/pedagogySkipReasonTone'
import {
  getPedagogyReviewSkipInsights,
  acknowledgePedagogyReviewQueueError,
  listAcknowledgedPedagogyReviewQueueErrorIds,
  type PedagogyReviewSkipInsights,
} from '@/services/pedagogyReviewSkipInsightsService'

type View = 'specialists' | 'partners' | 'developers'

function numberText(value: number, digits = 0): string {
  return value.toLocaleString('ru-RU', { maximumFractionDigits: digits })
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU')
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function ReasonCounts({ row }: {
  row: {
    recordingProblemCount: number
    noAccessCount: number
    noCameraCount: number
    lessonMismatchCount: number
    queueSelectionErrorCount: number
  }
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {row.recordingProblemCount > 0 && <ToneBadge size="pill" tone={TONE.recording_problem}>Нет записи: {row.recordingProblemCount}</ToneBadge>}
      {row.noAccessCount > 0 && <ToneBadge size="pill" tone={TONE.no_access}>Нет доступа: {row.noAccessCount}</ToneBadge>}
      {row.noCameraCount > 0 && <ToneBadge size="pill" tone={TONE.no_camera}>Нет камеры: {row.noCameraCount}</ToneBadge>}
      {row.lessonMismatchCount > 0 && <ToneBadge size="pill" tone={TONE.lesson_mismatch}>Не найдено: {row.lessonMismatchCount}</ToneBadge>}
      {row.queueSelectionErrorCount > 0 && <ToneBadge size="pill" tone={TONE.queue_selection_error}>Ошибка выдачи: {row.queueSelectionErrorCount}</ToneBadge>}
      {row.recordingProblemCount + row.noAccessCount + row.noCameraCount + row.lessonMismatchCount + row.queueSelectionErrorCount === 0 && (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  )
}

export function PedagogyReviewSkipInsightsPage() {
  const periods = useStudioPeriods()
  const [params, setParams] = useSearchParams()
  const [data, setData] = useState<PedagogyReviewSkipInsights | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { isSuperAdmin } = useSuperAdmin()
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<number>>(new Set())
  const weekNumber = Number(params.get('week')) || null
  const requestedView = params.get('skipView')
  const view: View = requestedView === 'partners' || requestedView === 'developers'
    ? requestedView
    : 'specialists'

  useEffect(() => {
    if (!periods.currentPeriod) return
    let cancelled = false
    setLoading(true)
    setError('')
    void Promise.all([
      getPedagogyReviewSkipInsights({
      periodId: periods.currentPeriod.period_id,
      weekNumber,
      }),
      isSuperAdmin ? listAcknowledgedPedagogyReviewQueueErrorIds() : Promise.resolve([]),
    ])
      .then(([value, ids]) => { if (!cancelled) { setData(value); setAcknowledgedIds(new Set(ids)) } })
      .catch(value => {
        if (!cancelled) setError(value instanceof Error ? value.message : 'Не удалось загрузить статистику')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [periods.currentPeriod, weekNumber, isSuperAdmin])

  const acknowledge = async (caseId: number) => {
    const comment = window.prompt('Что сделали или почему учли этот алерт?')?.trim()
    if (!comment) return
    try {
      await acknowledgePedagogyReviewQueueError(caseId, comment)
      setAcknowledgedIds(current => new Set([...current, caseId]))
      window.dispatchEvent(new CustomEvent('pedagogy-queue-error-acknowledged'))
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Не удалось отметить ошибку')
    }
  }

  const weeks = periods.currentPeriod
    ? Array.from({ length: Math.ceil((periods.currentPeriod.period_len_days ?? 28) / 7) }, (_, index) => index + 1)
    : []
  const skippedPercent = data && data.summary.assignedCount > 0
    ? data.summary.skippedCount / data.summary.assignedCount * 100
    : 0
  const specialists = useMemo(
    () => [...(data?.specialists ?? [])].sort((left, right) => right.skippedCount - left.skippedCount),
    [data]
  )
  const partners = useMemo(
    () => [...(data?.partners ?? [])].sort((left, right) => right.skippedCount - left.skippedCount),
    [data]
  )
  const developerIssues = data?.developerIssues ?? []

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value == null) next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  if (periods.loading) {
    return <Card><div className="py-10 text-center text-sm text-muted-foreground">Загружаем периоды…</div></Card>
  }

  return (
    <div className="space-y-4">
      <Card className="!p-3">
        <PeriodSelector
          currentPeriod={periods.currentPeriod}
          onPrevPeriod={periods.goToPrevPeriod}
          onNextPeriod={periods.goToNextPeriod}
          canGoPrev={periods.canGoPrev}
          canGoNext={periods.canGoNext}
          isCurrentPeriod={periods.isCurrentPeriod}
          compact
          showViewModeSelector
          periodFilterValue={weekNumber ? String(weekNumber) : 'period'}
          periodFilterOptions={[
            { value: 'period', label: 'Весь период' },
            ...weeks.map(week => ({ value: String(week), label: `Неделя ${week}` })),
          ]}
          onPeriodFilterChange={value => setParam('week', value === 'period' ? null : value)}
        />
      </Card>

      {error && <Card className="border-error/30 bg-error/5 text-sm text-error">{error}</Card>}
      {loading && !data && <Card><div className="py-10 text-center text-sm text-muted-foreground">Загружаем пропуски…</div></Card>}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Card className="!p-4"><div className="text-xs text-muted-foreground">Выдано</div><div className="mt-1 text-2xl font-semibold tabular-nums">{numberText(data.summary.assignedCount)}</div></Card>
            <Card className="!p-4"><div className="text-xs text-muted-foreground">Выполнено</div><div className="mt-1 text-2xl font-semibold tabular-nums">{numberText(data.summary.doneCount)}</div></Card>
            <Card className="!p-4"><div className="text-xs text-muted-foreground">Пропущено</div><div className="mt-1 text-2xl font-semibold tabular-nums">{numberText(data.summary.skippedCount)} <span className="text-sm font-medium text-muted-foreground">· {numberText(skippedPercent, 1)}%</span></div></Card>
            <Card className="border-warning/30 !p-4"><div className="text-xs text-muted-foreground">Ждут проверки</div><div className="mt-1 text-2xl font-semibold tabular-nums text-warning">{numberText(data.summary.awaitingVerificationCount)}</div></Card>
            <Card className="border-error/30 !p-4"><div className="text-xs text-muted-foreground">Ошибки выдачи</div><div className="mt-1 text-2xl font-semibold tabular-nums text-error">{numberText(data.summary.developerIssuesCount)}</div></Card>
          </div>

          <Card className="border-warning/30 bg-warning/5 !px-4 !py-3">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div>
                <div className="font-medium text-foreground">Пропуск считается сигналом, пока руководитель не проверил причину</div>
                <div className="mt-0.5 text-xs text-muted-foreground">В статистике отдельно показаны исходная причина специалиста и результат проверки руководителя.</div>
              </div>
            </div>
          </Card>

          <div className="flex gap-5 overflow-x-auto border-b border-border" role="tablist" aria-label="Статистика пропусков">
            {([
              ['specialists', 'По специалистам', data.specialists.length],
              ['partners', 'По франчайзи', data.partners.length],
              ['developers', 'Ошибки выдачи', developerIssues.length],
            ] as const).map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={view === key}
                onClick={() => setParam('skipView', key === 'specialists' ? null : key)}
                className={`shrink-0 border-b-2 px-0.5 pb-2.5 pt-1 text-sm font-medium transition-colors ${
                  view === key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {label} <span className="ml-1 text-xs tabular-nums text-muted-foreground">{count}</span>
              </button>
            ))}
          </div>

          {view === 'specialists' && (
            <Card className="overflow-hidden !p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1180px] text-sm">
                  <thead className="bg-muted/30 text-left text-xs text-muted-foreground"><tr><th className="px-4 py-3">Специалист</th><th className="px-4 py-3">Выдано</th><th className="px-4 py-3">Выполнено</th><th className="px-4 py-3">Пропущено</th><th className="px-4 py-3">Причины</th><th className="px-4 py-3">Проверка руководителя</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {specialists.map(row => (
                      <tr key={row.specialistUserId}>
                        <td className="px-4 py-3 font-medium">{row.specialistName}</td>
                        <td className="px-4 py-3 tabular-nums">{numberText(row.assignedCount)}</td>
                        <td className="px-4 py-3 tabular-nums">{numberText(row.doneCount)}</td>
                        <td className="px-4 py-3 tabular-nums"><span className="font-medium">{numberText(row.skippedCount)}</span><span className="ml-1 text-xs text-muted-foreground">· {numberText(row.skippedPercent, 1)}%</span></td>
                        <td className="px-4 py-3"><ReasonCounts row={row} /></td>
                        <td className="px-4 py-3"><div className="flex flex-wrap gap-1"><Badge variant="success">Подтверждено: {row.confirmedCount}</Badge><Badge variant="warning">Изменено: {row.changedCount}</Badge><Badge variant="error">Не подтвердилось: {row.notConfirmedCount}</Badge>{row.awaitingVerificationCount > 0 && <Badge variant="neutral">Ждёт: {row.awaitingVerificationCount}</Badge>}</div></td>
                      </tr>
                    ))}
                    {specialists.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">В выбранном диапазоне заданий нет.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {view === 'partners' && (
            <Card className="overflow-hidden !p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1120px] text-sm">
                  <thead className="bg-muted/30 text-left text-xs text-muted-foreground"><tr><th className="px-4 py-3">Франчайзи</th><th className="px-4 py-3">Пропусков</th><th className="px-4 py-3">Уникальных проблем</th><th className="px-4 py-3">Студий</th><th className="px-4 py-3">Причины</th><th className="px-4 py-3">Открыто</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {partners.map(row => (
                      <tr key={row.partnerNumber ?? 'unknown'}>
                        <td className="px-4 py-3 font-medium">{row.partnerName}{row.partnerNumber != null && <div className="text-xs font-normal text-muted-foreground">№ {row.partnerNumber}</div>}</td>
                        <td className="px-4 py-3 font-medium tabular-nums">{numberText(row.skippedCount)}</td>
                        <td className="px-4 py-3 tabular-nums">{numberText(row.uniqueProblemCount)}</td>
                        <td className="px-4 py-3 tabular-nums">{numberText(row.affectedStudiosCount)}</td>
                        <td className="px-4 py-3"><ReasonCounts row={row} /></td>
                        <td className="px-4 py-3">{row.openCasesCount > 0 ? <div><Badge variant="warning">{row.openCasesCount}</Badge><div className="mt-1 text-xs text-muted-foreground">старейший с {formatDate(row.oldestOpenAt)}</div></div> : <span className="inline-flex items-center gap-1 text-success"><CheckCircle2 size={14} />Нет</span>}</td>
                      </tr>
                    ))}
                    {partners.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">В выбранном диапазоне пропусков нет.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {view === 'developers' && (
            <Card className="overflow-hidden !p-0">
              <div className="border-b border-border px-4 py-3">
                <div className="flex items-start gap-2"><Code2 className="mt-0.5 h-4 w-4 text-muted-foreground" /><div><div className="text-sm font-semibold">Подтверждённые ошибки правил выдачи</div><div className="mt-0.5 text-xs text-muted-foreground">Общий список без назначения исполнителя и внутренних статусов разработки.</div></div></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1080px] text-sm">
                  <thead className="bg-muted/30 text-left text-xs text-muted-foreground"><tr><th className="px-4 py-3">Студия / франчайзи</th><th className="px-4 py-3">Занятие</th><th className="px-4 py-3">Почему не должно выдаваться</th><th className="px-4 py-3">Подтверждение</th>{isSuperAdmin && <th className="px-4 py-3">Статус</th>}</tr></thead>
                  <tbody className="divide-y divide-border">
                    {developerIssues.map(item => (
                    <tr key={item.caseId}>
                        <td className="px-4 py-3 font-medium">Студия №{item.studioNumber}<div className="text-xs font-normal text-muted-foreground">{item.cityName ? `${item.cityName} · ` : ''}{item.partnerName}</div></td>
                        <td className="px-4 py-3">{formatDate(item.trainingDate)}, {item.trainingTime.slice(0, 5)}<div className="text-xs text-muted-foreground">{item.directionName ?? item.teacherName ?? '—'}</div></td>
                        <td className="max-w-[440px] whitespace-pre-wrap px-4 py-3">{item.queueAction === 'exclude_lesson_request_studio' && <Badge variant="warning" className="mb-1.5">Просят убрать студию</Badge>}<div>{item.details ?? item.decisionComment ?? '—'}</div></td>
                      <td className="px-4 py-3"><div>{item.decidedByName ?? 'Руководитель'}</div><div className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock3 size={13} />{formatDateTime(item.decidedAt)}</div></td>
                      {isSuperAdmin && <td className="px-4 py-3">{acknowledgedIds.has(item.caseId) ? <Badge variant="success">Учтено</Badge> : <button type="button" onClick={() => void acknowledge(item.caseId)} className="rounded-md bg-amber-400 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-amber-300">Отметить</button>}</td>}
                      </tr>
                    ))}
                    {developerIssues.length === 0 && <tr><td colSpan={isSuperAdmin ? 5 : 4} className="px-4 py-10 text-center text-muted-foreground">Подтверждённых ошибок выдачи за период нет.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
